import { useQuery } from "@apollo/client";
import { XIcon } from "@heroicons/react/solid";
import { useCallback, useEffect, useRef, useState } from "react";
import {
  AccountToOrganizationMembershipStatus,
  GetOrganizationMembersQueryQuery,
  GetOrganizationMembersQueryQueryVariables,
  OrgSettingsOrganizationFragmentFragment,
} from "types/graphql-schema";

import useLabel from "@apps/use-label/use-label";
import cache from "@cache/cache";
import Button from "@components/button/button";
import Input from "@components/input/input";
import Loading from "@components/loading/loading";
import {
  Table,
  TableBody,
  TableContainer,
  TableHeadCell,
  TableHeadRow,
  TableSortDir,
  TableSticky,
} from "@components/table/table";
import useDebounce from "@components/use-debounce/use-debounce";
import useWindowSize from "@components/use-window-size/use-window-size";
import useUserComboboxQuery from "@components/user-combobox/use-user-combobox-query";
import UserCombobox from "@components/user-combobox/user-combobox";
import {
  UserComboboxOption,
  UserComboboxOptionType,
  UserComboboxTeamOption,
  UserComboboxUserOption,
} from "@components/user-combobox/user-combobox-list";
import { graphqlNone } from "@helpers/constants";
import { classNames } from "@helpers/css";
import { assertEdgesNonNull } from "@helpers/helpers";

import InviteDialog from "../../invite-dialog/invite-dialog";
import getOrganizationMembersQuery from "../graphql/get-organization-members-query";
import MemberItem from "./member-item";

enum MemberColumns {
  Name = "name",
  StartDate = "startDate",
  EmployeeID = "employeeId",
  Position = "position",
  RolePermissions = "rolePermissions",
  CurrentRoleTrack = "currentRoleTrack",
  CurrentRole = "currentRole",
}

type TableSort = { column: MemberColumns; direction: TableSortDir };

const getOrderBy = (sort: TableSort) => {
  const direction = `${sort.direction === "desc" ? "-" : ""}`;
  switch (sort.column) {
    case MemberColumns.Name:
      return `${direction}user__first_name`;
    case MemberColumns.StartDate:
      return `${direction}start_date`;
    case MemberColumns.EmployeeID:
      return `${direction}employee_id`;
    case MemberColumns.Position:
      return `${direction}position`;
    case MemberColumns.RolePermissions:
      return `${direction}role`;
    case MemberColumns.CurrentRoleTrack:
      return `${direction}current_role_track`;
    case MemberColumns.CurrentRole:
      return `${direction}current_role_name`;
  }
};

const FilterChip = ({
  active,
  label,
  onToggle,
}: {
  active: boolean;
  label: string;
  onToggle: () => void;
}) => (
  <button
    type="button"
    onClick={onToggle}
    className={classNames(
      "flex items-center gap-1 rounded-full px-3 h-8 text-sm transition-colors border",
      active
        ? "bg-blue-50 text-blue-700 border-blue-200 hover:bg-blue-100"
        : "border-dashed border-gray-300 text-gray-500 hover:text-gray-700 hover:border-gray-400"
    )}
  >
    {active && (
      <span className="flex items-center justify-center h-5 w-5 rounded-full bg-blue-100 text-blue-700">
        <XIcon className="h-3 w-3" />
      </span>
    )}
    {label}
  </button>
);

const Members = ({
  organization,
}: {
  organization: OrgSettingsOrganizationFragmentFragment;
}) => {
  const tableContainerRef = useRef<HTMLDivElement>(null);
  const tablePaginationContainerRef = useRef<HTMLDivElement>(null);
  const label = useLabel();
  const [search, setSearch] = useState("");
  const debouncedSearch = useDebounce(search, 500);
  const [showAdminsOnly, setShowAdminsOnly] = useState(false);
  const [showInviteDialog, setShowInviteDialog] = useState(false);
  const [showActiveMembersOnly, setShowActiveMembersOnly] = useState(true);
  const [sort, setSort] = useState<TableSort>({
    column: MemberColumns.Name,
    direction: "asc",
  });
  const [managerFilter, setManagerFilter] = useState<UserComboboxOption | null>(
    null
  );
  const [team, setTeam] = useState<UserComboboxOption | null>(null);
  const emptyTeamOption: UserComboboxTeamOption = {
    id: graphqlNone,
    type: UserComboboxOptionType.TEAM,
    title: `No ${label("team", { pluralize: 0 })}`,
  };
  const emptyManagerOption: UserComboboxUserOption = {
    id: graphqlNone,
    type: UserComboboxOptionType.USER,
    name: `No managers`,
  };
  const {
    options: teamOptions,
    query: teamQuery,
    setQuery: setTeamQuery,
  } = useUserComboboxQuery({
    types: [UserComboboxOptionType.TEAM],
    emptyOption: emptyTeamOption,
  });
  const {
    options: managerOptions,
    query: managerQuery,
    setQuery: setManagerQuery,
  } = useUserComboboxQuery({
    types: [UserComboboxOptionType.USER],
    emptyOption: emptyManagerOption,
  });

  const { data, loading, fetchMore, variables } = useQuery<
    GetOrganizationMembersQueryQuery,
    GetOrganizationMembersQueryQueryVariables
  >(getOrganizationMembersQuery, {
    variables: {
      search: debouncedSearch,
      after: null,
      organizationId: organization.id,
      managerId:
        managerFilter && typeof managerFilter.id === "number"
          ? managerFilter.id
          : undefined,
      orderBy: getOrderBy(sort),
      showAdminsOnly: showAdminsOnly ? true : undefined,
      membershipStatus: showActiveMembersOnly
        ? AccountToOrganizationMembershipStatus.Active
        : undefined,
      teamId: team && typeof team.id === "number" ? team.id : undefined,
    },
  });
  const members = data ? assertEdgesNonNull(data.organization?.members) : [];

  const handleChangeShowAdminsOnly = useCallback((value: boolean) => {
    setShowAdminsOnly(value);
  }, []);

  const handleChangeShowActiveMembersOnly = useCallback((value: boolean) => {
    setShowActiveMembersOnly(value);
  }, []);

  const handleClickMore = () => {
    const after = data?.organization?.members.pageInfo.endCursor;
    fetchMore({
      variables: { after, merge: true },
    });
  };

  const handleRemoveUser = (member: { id: number }) => {
    cache.updateQuery(
      {
        query: getOrganizationMembersQuery,
        variables,
      },
      (data) => {
        const newEdges = data.organization.members.edges.filter(
          (edge: any) => edge?.node.id !== member.id
        );
        const newData = {
          ...data,
          organization: {
            ...data.organization,
            totalCount: data.organization.totalCount - 1,
            members: {
              ...data.organization.members,
              edges: newEdges,
            },
          },
        };
        return newData;
      }
    );
  };

  const handleSort = useCallback(
    (column: MemberColumns) => {
      const direction =
        sort.column === column && sort.direction === "asc" ? "desc" : "asc";
      setSort({ column, direction });
    },
    [sort]
  );

  const { height: windowHeight } = useWindowSize();

  useEffect(() => {
    if (tableContainerRef.current) {
      const tableRect = tableContainerRef.current.getBoundingClientRect();
      const paginationRectHeight =
        tablePaginationContainerRef.current?.getBoundingClientRect().height ||
        0;
      const padding = 6 * 4;
      const maxHeight =
        window.innerHeight - tableRect.top - paginationRectHeight - padding;
      tableContainerRef.current.style.maxHeight = `${maxHeight}px`;
    }
  }, [data, windowHeight]);

  return (
    <div className="" aria-label="Org settings members">
      {showInviteDialog && (
        <InviteDialog
          organization={organization}
          onClose={() => {
            setShowInviteDialog(false);
          }}
        />
      )}
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="flex flex-col gap-2 flex-1 min-w-[260px]">
          <div className="flex flex-wrap items-center gap-4">
            <div className="w-36 shrink-0">
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search..."
              />
            </div>
            <div className="w-36 shrink-0">
              <UserCombobox
                query={teamQuery}
                value={team}
                options={teamOptions}
                clearable={team !== null}
                onChangeQuery={setTeamQuery}
                onChangeValue={setTeam}
                onClearValue={() => setTeam(null)}
                placeholder={`${label("team", { capitalize: true })}...`}
              />
            </div>
            <div className="w-36 shrink-0">
              <UserCombobox
                query={managerQuery}
                value={managerFilter}
                options={managerOptions}
                clearable={managerFilter !== null}
                onChangeQuery={setManagerQuery}
                onChangeValue={setManagerFilter}
                onClearValue={() => setManagerFilter(null)}
                placeholder={`Manager...`}
              />
            </div>
          </div>
          <div className="flex gap-2">
            <FilterChip
              active={showAdminsOnly}
              label="Admins Only"
              onToggle={() => handleChangeShowAdminsOnly(!showAdminsOnly)}
            />
            <FilterChip
              active={showActiveMembersOnly}
              label="Active Only"
              onToggle={() =>
                handleChangeShowActiveMembersOnly(!showActiveMembersOnly)
              }
            />
          </div>
        </div>

        <div className="flex items-center gap-4 ml-auto">
          <Button to={`/org-chart`} text="Org Chart" />
          <Button
            theme="primary"
            text="Invite"
            onClick={() => {
              setShowInviteDialog(true);
            }}
          />
        </div>
      </div>

      {loading && !data && (
        <div className="flex justify-center mt-8">
          <Loading mini size="5" />
        </div>
      )}
      {data && (
        <TableContainer
          scroll
          className="mt-6 max-h-[300px]"
          ref={tableContainerRef}
        >
          <Table className="w-full" sticky={TableSticky.topAndLeft}>
            <TableHeadRow className="">
              <TableHeadCell
                sorted={
                  sort.column === MemberColumns.Name ? sort.direction : null
                }
                onClick={() => handleSort(MemberColumns.Name)}
                cellClassName="min-w-[250px]"
              >
                Name
              </TableHeadCell>
              <TableHeadCell cellClassName="min-w-[200px]">
                Manager
              </TableHeadCell>
              <TableHeadCell cellClassName="min-w-[200px]">
                {label("team", { pluralize: true, capitalize: true })}
              </TableHeadCell>
              <TableHeadCell
                cellClassName="min-w-[120px] "
                sorted={
                  sort.column === MemberColumns.EmployeeID
                    ? sort.direction
                    : null
                }
                onClick={() => handleSort(MemberColumns.EmployeeID)}
              >
                Employee ID
              </TableHeadCell>
              <TableHeadCell
                cellClassName="min-w-[120px]"
                sorted={
                  sort.column === MemberColumns.Position ? sort.direction : null
                }
                onClick={() => handleSort(MemberColumns.Position)}
              >
                Position
              </TableHeadCell>
              <TableHeadCell
                cellClassName="min-w-[200px]"
                sorted={
                  sort.column === MemberColumns.CurrentRoleTrack
                    ? sort.direction
                    : null
                }
                onClick={() => handleSort(MemberColumns.CurrentRoleTrack)}
              >
                Career Track
              </TableHeadCell>
              <TableHeadCell
                cellClassName="min-w-[200px]"
                sorted={
                  sort.column === MemberColumns.CurrentRole
                    ? sort.direction
                    : null
                }
                onClick={() => handleSort(MemberColumns.CurrentRole)}
              >
                Role
              </TableHeadCell>
              <TableHeadCell
                cellClassName="min-w-[150px]"
                sorted={
                  sort.column === MemberColumns.StartDate
                    ? sort.direction
                    : null
                }
                onClick={() => handleSort(MemberColumns.StartDate)}
              >
                Start Date
              </TableHeadCell>
              <TableHeadCell
                cellClassName="min-w-[80px]"
                sorted={
                  sort.column === MemberColumns.RolePermissions
                    ? sort.direction
                    : null
                }
                onClick={() => handleSort(MemberColumns.RolePermissions)}
              >
                Permission
              </TableHeadCell>
              <TableHeadCell>Actions</TableHeadCell>
            </TableHeadRow>
            <TableBody>
              {data &&
                members.map((member) => (
                  <MemberItem
                    member={member}
                    organization={organization}
                    key={member.id}
                    onRemoveUser={handleRemoveUser}
                  />
                ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
      {data?.organization?.members.pageInfo.hasNextPage && (
        <div
          className="flex items-center py-2"
          ref={tablePaginationContainerRef}
        >
          <button
            className="text-gray-500 text-sm mr-4 hover:underline"
            onClick={handleClickMore}
            disabled={loading}
          >
            Load more members...
          </button>
          {loading && <Loading size="5" mini />}
        </div>
      )}
    </div>
  );
};

export default Members;
